<template>
    <v-sheet>
        <h5 class="page-text--lg mb-8 mb-lg-16 line-height-15">매칭정보</h5>
        <v-divider class="primary border-2" />
        <v-data-table
            v-bind="{
                headers,

                items,
                itemKey: '_id',
                itemsPerPage: -1,

                loading,
            }"
            hide-default-footer
            disable-filtering
            disable-sort
        >
            <template v-for="header in headers.filter((header) => header.hasOwnProperty('formatter'))" #[`item.${header.value}`]="{ value }"> {{ header.formatter(value) }} </template>

            <template #[`item.employee`]="{ value }">
                <v-layout column align-center>
                    <span class="mb-4 mb-lg-8"> {{ value.info.name }} </span>
                    <expo-employee-view
                        v-bind="{
                            _employee: value._id,
                            _program,
                        }"
                    >
                        <template #activator="{ attrs, on }">
                            <v-btn small outlined width="100" color="grey-d" v-bind="attrs" v-on="on">
                                <span class="grey--text"> 지원정보 </span>
                            </v-btn>
                        </template>
                    </expo-employee-view>
                </v-layout>
            </template>

            <template #[`item.employer`]="{ value }">
                <v-layout column align-center>
                    <span class="mb-4 mb-lg-8"> {{ value.info.name }} </span>
                    <v-btn small outlined width="100" color="grey-d" :to="`/companies/${value._user}`">
                        <span class="grey--text"> 기업정보 </span>
                    </v-btn>
                </v-layout>
            </template>

            <template #[`item.desc`]="{ item }">
                <interview-desc
                    v-bind="{
                        item,
                        isUserEmployee,
                        isUserEmployer,
                    }"
                />
            </template>

            <template #[`item.state`]="{ item }">
                <expo-matching-state
                    v-bind="{
                        value: item,
                        program,
                        employee: item.employee,
                        employer: item.employer,
                        isUserHost,
                        isUserEmployee,
                        isUserEmployer,
                    }"
                />
            </template>

            <template #[`item.reply`]="{ item }">
                <expo-matching-reply
                    v-bind="{
                        value: item,
                        program,
                        isUserHost,
                        isUserEmployee,
                        isUserEmployer,
                    }"
                    @input="pushMatchings"
                />
            </template>

            <template #footer>
                <v-divider />
            </template>
        </v-data-table>
    </v-sheet>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import { initExpoMatching } from "@/assets/variables";

import ExpoEmployeeView from "@/components/client/expo/employee/expo-employee-view.vue";
import ExpoMatchingReply from "@/components/client/expo/matching/expo-matching-reply.vue";
import ExpoMatchingState from "@/components/client/expo/matching/expo-matching-state.vue";
import InterviewDesc from "./interview/interview-desc.vue";
import MatchingMeta from "./matchings/matching-meta.vue";

export default {
    components: {
        ExpoEmployeeView,
        ExpoMatchingReply,
        ExpoMatchingState,
        InterviewDesc,
        MatchingMeta,
    },
    props: {
        isTabActive: { type: Boolean, default: false },
    },
    data: () => ({
        loading: false,
    }),
    computed: {
        ...mapState("expo", ["program", "matchings"]),
        ...mapGetters("expo", ["_program", "isUserHost", "isUserEmployee", "isUserEmployer"]),
        ...mapGetters(["doesUserHaveConsoleScope"]),

        headers() {
            const items = {
                employee: {
                    text: "지원자",
                    value: "employee",
                    width: this.isUserEmployer ? 180 : 140,
                },

                employer: {
                    text: "기업",
                    value: "employer",
                    width: this.isUserEmployee ? 180 : 140,
                },

                phone: {
                    text: "연락처",
                    value: (this.isUserEmployer ? "employee" : "employer") + ".info.phone",
                    width: 180,
                    cellClass: "word-break-break-all",
                },

                email: {
                    text: "이메일",
                    value: (this.isUserEmployer ? "employee" : "employer") + ".info.email",
                    width: 180,
                    cellClass: "word-break-break-all",
                },

                desc: {
                    text: "매칭상세",
                    value: "desc",
                },

                state: {
                    text: this.isUserHost || this.doesUserHaveConsoleScope ? "상태" : "면접요청",
                    value: "state",
                    width: 180,
                },

                reply: {
                    text: "채용여부",
                    value: "reply",
                    width: 120,
                },
            };

            let headers = [];

            if (this.isUserHost || this.doesUserHaveConsoleScope) {
                headers = [
                    //
                    items.employer,
                    items.employee,
                    items.desc,
                    items.state,
                    items.reply,
                ];
            }
            //
            else if (this.isUserEmployee) {
                headers = [
                    //
                    items.employer,
                    items.phone,
                    items.email,
                    items.desc,
                    items.state,
                ];
            }
            //
            else if (this.isUserEmployer) {
                headers = [
                    //
                    items.employee,
                    items.phone,
                    items.email,
                    items.desc,
                    items.state,
                    items.reply,
                ];
            }

            return headers.map((item) => ({
                ...item,
                align: item.align ?? "center",
                cellClass: (item.cellClass || "") + " px-10 px-lg-20 py-8 py-lg-16",
            }));
        },
        items() {
            return this.matchings.filter(({ isGranted }) => isGranted).map(initExpoMatching);
        },
    },
    methods: {
        ...mapMutations("expo", ["pushMatchings"]),
        ...mapActions("expo", ["getMatchings"]),
        ...mapActions("expo", { expoInit: "init" }),

        init() {
            this.search();
        },

        async search() {
            if (!this.isTabActive) return;

            this.loading = true;
            try {
                await this.getMatchings();
            } finally {
                this.loading = false;
            }
        },

        updateItem(matching) {
            this.pushMatchings(matching);

            this.search();
            this.expoInit();
        },
    },
    watch: {
        isTabActive() {
            this.search();
        },
    },
    mounted() {
        this.init();
    },
};
</script>


<style lang="scss" scoped>
::v-deep .word-break-break-all {
    word-break: break-all;
}
::v-deep .white-space-pre-line {
    white-space: pre-line;
}
</style>